import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import {
    FiBarChart2,
    FiCreditCard,
    FiHome,
    FiMessageCircle,
    FiPlus,
    FiSettings,
    FiTrendingUp,
    FiTruck,
    FiUpload,
    FiUserPlus,
    FiUsers,
    FiX
} from 'react-icons/fi';
import { HiOutlineSparkles } from 'react-icons/hi';
import { NavLink, useLocation } from 'react-router-dom';
import { getApiUrl } from '../../config/api.config';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ setSidebarOpen }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [unreadEnquiriesCount, setUnreadEnquiriesCount] = useState(0);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('agent_token');
        if (!token) return;

        const response = await fetch(getApiUrl('/chats/agent/unread-count'), {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) return;

        const data = await response.json();
        setUnreadEnquiriesCount(data?.data?.unreadCount || 0);
      } catch (error) {
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 15000);
    return () => clearInterval(interval);
  }, []);

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: FiHome,
      current: location.pathname === '/dashboard'
    },
    {
      name: 'Car Management',
      icon: FiTruck,
      children: [
        { name: 'All Cars', href: '/dashboard/cars', icon: FiTruck },
        { name: 'Add Car', href: '/dashboard/cars/add', icon: FiPlus },
        { name: 'Bulk Upload', href: '/dashboard/cars/bulk-upload', icon: FiUpload }
      ]
    },
    {
      name: 'Seller Management',
      icon: FiUsers,
      children: [
        { name: 'All Sellers', href: '/dashboard/sellers', icon: FiUsers },
        { name: 'Add Seller', href: '/dashboard/sellers/add', icon: FiUserPlus }
      ]
    },
    {
      name: 'Enquiries',
      href: '/dashboard/enquiries',
      icon: FiMessageCircle,
      current: location.pathname === '/dashboard/enquiries',
      badge: unreadEnquiriesCount
    },
    {
      name: 'Analytics',
      href: '/dashboard/analytics',
      icon: FiBarChart2,
      current: location.pathname === '/dashboard/analytics'
    },
    {
      name: 'Price Suggestion',
      href: '/dashboard/price-suggestion',
      icon: FiTrendingUp,
      current: location.pathname === '/dashboard/price-suggestion'
    },
    {
      name: 'My Subscription',
      href: '/dashboard/subscribe',
      icon: FiCreditCard,
      current: location.pathname === '/dashboard/subscribe'
    },
    {
      name: 'Settings',
      href: '/dashboard/settings',
      icon: FiSettings,
      current: location.pathname === '/dashboard/settings'
    }
  ];

  const sidebarVariants = {
    open: {
      width: 256,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 40
      }
    },
    closed: {
      width: 80,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 40
      }
    }
  };

  return (
    <motion.div
      variants={sidebarVariants}
      className="flex h-full flex-col bg-white border-r border-gray-200"
    >
      {/* Logo */}
      <div className="flex h-16 items-center justify-between px-6 border-b border-gray-200">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center space-x-3"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl gradient-bg">
            <HiOutlineSparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold gradient-text">UsedAdda</h1>
            <p className="text-xs text-gray-500">Agent Portal</p>
          </div>
        </motion.div>
        <button
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-600"
        >
          <FiX className="h-5 w-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-2 px-4 py-6">
        {navigationItems.map((item, index) => (
          <div key={item.name}>
            {item.href ? (
              <NavLink
                to={item.href}
                className={({ isActive }) =>
                  `group flex items-center justify-between px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                    isActive
                      ? 'gradient-bg text-white shadow-lg'
                      : 'text-gray-700 hover:bg-orange-50 hover:text-orange-600'
                  }`
                }
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex items-center"
                >
                  <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                  {item.name}
                </motion.div>
                {item.badge && item.badge > 0 && (
                  <span className="inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ) : (
              <div className="space-y-1">
                <div className="flex items-center px-3 py-2 text-sm font-medium text-gray-900">
                  <item.icon className="mr-3 h-5 w-5 text-gray-400" />
                  {item.name}
                </div>
                <div className="space-y-1 pl-6">
                  {item.children?.map((child) => (
                    <NavLink
                      key={child.name}
                      to={child.href}
                      end
                      className={({ isActive }) =>
                        `group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'gradient-bg text-white shadow-md'
                            : 'text-gray-600 hover:bg-orange-50 hover:text-orange-600'
                        }`
                      }
                    >
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center w-full"
                      >
                        <child.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                        {child.name}
                      </motion.div>
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* User section */}
      <div className="border-t border-gray-200 p-4">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 rounded-full gradient-bg flex items-center justify-center">
            <span className="text-sm font-medium text-white">
              {(user?.fullName || user?.name || user?.firstName || 'A').charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user?.fullName || user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Agent User'}
            </p>
            <p className="text-xs text-gray-500 truncate">
              {user?.email || 'agent@usedadda.com'}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Sidebar;